/* PYNRS (2026-10): mobile swipe on product card photos (and, since 2026-11-02, the product's video).
   The card renders this colour's photos as slides (snippets/product-card.liquid). When a colour swatch is picked, the
   theme swaps the first photo itself; this script rebuilds the extra slides and the dots for the new colour from the
   product JSON (fetched once per product). */
(() => {
  const SLIDE_LIMIT = 4;
  const WIDTHS = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1200];
  const products = new Map();

  const loadProduct = (handle) => {
    if (!products.has(handle)) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      products.set(handle, fetch(`${root}products/${handle}.js`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))));
    }
    return products.get(handle);
  };

  const absolute = (src) => (src.startsWith('//') ? `https:${src}` : src);
  const srcset = (src, maxWidth) =>
    WIDTHS.filter((w) => w <= maxWidth)
      .map((w) => `${src}${src.includes('?') ? '&' : '?'}width=${w} ${w}w`)
      .join(', ');
  const colorSlug = (name) => `#color_${name.trim().toLowerCase().replace(/[\s-]+/g, '_')}`;
  // alt tags written with spaces or hyphens ("#color_midnight moss") match the underscore slug
  const normalizeAlt = (alt) => (alt || '').toLowerCase().replace(/[\s-]+/g, '_');

  // Card videos (mobile swipe): muted, inline, looping, lazy; they play only while they are the slide in view.
  const videoSlide = (m) => {
    const video = document.createElement('video');
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('loop', '');
    video.preload = 'none';
    if (m.preview_image) {
      video.poster = absolute(m.preview_image.src) + (m.preview_image.src.includes('?') ? '&' : '?') + 'width=800';
      video.style.backgroundImage = `url(${video.poster})`;
    }
    video.className = 'product-card__image product-card__image--slide product-card__video aspect-natural';
    (m.sources || []).forEach((source) => {
      const el = document.createElement('source');
      el.dataset.src = absolute(source.url);
      if (source.mime_type) el.type = source.mime_type;
      video.appendChild(el);
    });
    const slide = document.createElement('div');
    slide.className = 'product-card__slide product-card__slide--video';
    slide.appendChild(video);
    watch(slide);
    return slide;
  };

  // sources wait in data-src so nothing downloads before the slide reaches the screen; they are attached as soon
  // as it starts sliding in, so the first frames are ready by the time the swipe lands (no blank flash, Mayra)
  const prepare = (video) => {
    const pending = video.querySelectorAll('source[data-src]');
    if (!pending.length) return;
    pending.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.preload = 'auto';
    video.load();
  };

  const inView = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
          if (isIntersecting) prepare(target);
          if (isIntersecting && intersectionRatio >= 0.6) {
            target.muted = true;
            const played = target.play();
            if (played && played.catch) played.catch(() => {});
          } else if (!target.paused) {
            target.pause();
          }
        });
      }, { threshold: [0, 0.6] })
    : null;

  const watch = (root) => {
    if (!inView || !root.querySelectorAll) return;
    const videos = root.matches && root.matches('.product-card__slide video') ? [root] : root.querySelectorAll('.product-card__slide video');
    videos.forEach((video) => inView.observe(video));
  };

  const start = () => {
    watch(document);
    // cards that arrive later (filters, pagination, quick filters) bring their own videos
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => node.nodeType === 1 && watch(node)));
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('change', async (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio' || !radio.closest('.product-card__swatch-list')) return;

    const card = radio.closest('product-card');
    const carousel = card && card.querySelector('.product-card__slides');
    const figure = card && card.querySelector('.product-card__figure');
    if (!card || !carousel || !figure) return;

    const label = (radio.labels && radio.labels[0] && radio.labels[0].textContent.trim()) || '';
    if (!label) return;

    let product;
    try {
      product = await loadProduct(card.getAttribute('handle'));
    } catch (e) {
      return;
    }

    const slug = colorSlug(label);
    // images tagged for this colour, plus videos tagged for it or not tagged for any colour (Mayra, 2026-11-02)
    const belongs = (m) => {
      const alt = normalizeAlt(m.alt);
      if (m.media_type === 'image') return alt.includes(slug);
      if (m.media_type === 'video') return !alt.includes('#color_') || alt.includes(slug);
      return false;
    };
    let list = product.media.filter(belongs);
    if (!list.some((m) => m.media_type === 'image')) {
      list = product.media.filter((m) => m.media_type === 'image' || (m.media_type === 'video' && belongs(m)));
    }
    list = list.slice(0, SLIDE_LIMIT);

    // the theme replaces the first photo itself; skip the variant's own photo among the extras
    let primaryId = null;
    try {
      if (radio.hasAttribute('data-variant-media')) {
        const variantMedia = JSON.parse(radio.getAttribute('data-variant-media'));
        primaryId = variantMedia.id;
        if (variantMedia.preview_image && variantMedia.preview_image.width) {
          carousel.style.setProperty('--card-photo-aspect', `${variantMedia.preview_image.width} / ${variantMedia.preview_image.height}`);
        }
      }
    } catch (e) {}
    // video-first cards (product metafield custom.card_video_first) open on the video; once a shopper picks a colour the
    // card leads with that colour's photo so the tap visibly works (Mayra), and the video moves into the swipe
    if (carousel.hasAttribute('data-video-first')) carousel.removeAttribute('data-video-first');
    const videoFirst = null;
    const extras = list
      .filter((m) => m.id !== primaryId && (!videoFirst || m.id !== videoFirst.id))
      .slice(0, SLIDE_LIMIT - (videoFirst ? 2 : 1));

    // keep only the photo slide the theme swaps on swatch change, rebuild the rest around it
    const primarySlide = (carousel.querySelector('.product-card__image--primary') || carousel.querySelector('.product-card__image')).closest('.product-card__slide');
    carousel.querySelectorAll('.product-card__slide').forEach((s) => { if (s !== primarySlide) s.remove(); });
    if (videoFirst) carousel.insertBefore(videoSlide(videoFirst), primarySlide);
    const first = primarySlide.querySelector('.product-card__image');
    const sizes = (first && first.getAttribute('sizes')) || '100vw';

    extras.forEach((m) => {
      if (m.media_type === 'video') {
        carousel.appendChild(videoSlide(m));
        return;
      }
      const img = new Image(m.preview_image.width, m.preview_image.height);
      const src = absolute(m.preview_image.src);
      img.src = src;
      img.srcset = srcset(src, m.preview_image.width);
      img.sizes = sizes;
      img.loading = 'lazy';
      img.alt = m.alt || '';
      img.draggable = false;
      img.className = 'product-card__image product-card__image--slide aspect-natural';
      const slide = document.createElement('div');
      slide.className = 'product-card__slide';
      slide.appendChild(img);
      carousel.appendChild(slide);
    });

    const oldDots = figure.querySelector('.product-card__dots');
    if (oldDots) oldDots.remove();

    const count = extras.length + 1 + (videoFirst ? 1 : 0);
    if (count > 1) {
      const nav = document.createElement('carousel-navigation');
      nav.className = 'product-card__dots scroll-marker-group';
      nav.setAttribute('aria-controls', carousel.id);
      for (let i = 0; i < count; i++) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'scroll-marker tap-area tap-area--sm';
        button.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        const label = document.createElement('span');
        label.className = 'sr-only';
        label.textContent = `Go to item ${i + 1}`;
        button.appendChild(label);
        nav.appendChild(button);
      }
      figure.appendChild(nav);
    }

    carousel.scrollTo({ left: 0, behavior: 'auto' });
  });
})();
